/**
 * Payout curve — the settable ladder that turns a metric into tiered pay.
 *
 * An admin adds this under a custom component's Advanced settings and SETS the
 * tiers themselves: "at this attainment (or this dollar figure), pay this
 * amount." It is the milestone idea — a threshold that pays — drawn as a curve
 * and driven live by the component's own metric.
 *
 * A tier's threshold carries its own unit:
 *   "%"  → a percent of the metric's target      (at 110 = 110% of target)
 *   "$"  → a raw value of the metric             (at 300000 = $300,000)
 * so a team on a percent-of-goal plan and a team on hard dollar breakpoints use
 * the same control. Both resolve to one absolute value (see thresholdValue), so
 * the marker and the projected payout read off a single axis.
 *
 * Everything here is pure — no React, no fetching — so the editor's preview and
 * the card agree by construction. The per-tier payout the SERVER credits is a
 * separate step (same handoff as the bonus windows); this file is the shape,
 * the geometry, and the on-screen projection.
 */

export const PAYOUT_UNITS = ["%", "$"];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/* How the metric and the payout curve sit together in one card: side by side or
   stacked, which one leads (swap), and the split ratio the leading piece takes.
   Structured on purpose — the ratio is clamped so a card can never collapse a
   piece to nothing or drift out of alignment across the board. */
export const defaultPieceLayout = () => ({ dir: "side", swap: false, split: 0.42 });

export const sanitizePieceLayout = (pl) => {
  if (!pl || typeof pl !== "object") return null;
  const dir = pl.dir === "stack" ? "stack" : "side";
  const swap = !!pl.swap;
  let split = num(pl.split) || 0.42;
  split = Math.max(0.25, Math.min(0.75, split));
  return { dir, swap, split };
};

/* A gate is a condition a tier carries: until it is satisfied, that tier's
   accelerator is not allowed to pay and the bonus falls back to the highest
   tier below it that is clear.
 *
 * A gate has five settable parts and nothing else:
 *
 *   source   "metric"  — read a number off a KPI
 *            "manual"  — no number exists, so a person marks it met each period
 *   min      the floor that unlocks the tier. This is the only number that
 *            gates money.
 *   target   what the rep is actually chasing. Optional on purpose: an empty
 *            field IS "there is no target", which is why there is no toggle.
 *   period   the window the gate is judged over, its own, never the board's —
 *            a viewer changing their date filter must not change who gets paid.
 *   mode     "avg" | "sum" — when the same-named metric lives on more than one
 *            of the card's own assigned workspaces (Closed Rate on Field Sales
 *            AND In-House Sales), this decides how those readings pool into
 *            the one number the gate judges. Same convention as a metric
 *            plan's per-column mode (helpers/metricPlan.js).
 *
 * Manual gates carry no min and no target (there is no number to read) and are
 * confirmed per period, so an old confirmation can never pay a new quarter. */
export const GATE_PERIODS = [
  { value: "quarter", label: "This quarter", hint: "The three months the bonus is paid on." },
  { value: "year", label: "This year", hint: "Year to date, for anything judged annually." },
  { value: "month", label: "This month", hint: "The current month only." },
  { value: "custom", label: "Custom range", hint: "Two dates you set yourself." },
];

export const GATE_SOURCES = [
  { value: "metric", label: "Reads a metric", hint: "Pulled from the team's numbers automatically." },
  { value: "manual", label: "Confirmed by a person", hint: "No metric for it, so someone marks it each period." },
];

export const GATE_MODES = [
  { value: "avg", label: "Average across workspaces" },
  { value: "sum", label: "Sum across workspaces" },
];

export const gatePeriodLabel = (p) =>
  (GATE_PERIODS.find((o) => o.value === p) || GATE_PERIODS[0]).label;

export const defaultGate = () => ({
  name: "New gate",
  source: "metric",
  metric: "",
  metricName: "",
  unit: "%",
  min: null,
  target: null,
  mode: "avg",
  period: "quarter",
  from: "",
  to: "",
  on: true,
  confirmations: {},
});

/* Which unit a gate should carry for a metric, so the admin never has to say
   what the system already knows. Mirrors the app's own money test in
   useDashboardMetrics (revenue, sales, amount, cost, price, value, mrr, $) and
   adds ticket, which is money too. Everything else is a rate, so percent. */
export const unitForMetricLabel = (label) =>
  (/revenue|sales|amount|cost|price|value|mrr|ticket|\$/i.test(String(label || "")) ? "$" : "%");

/* Gates used to carry one number called `threshold`. Records saved before the
   min/target split still have it, and they are only rewritten on the next save,
   so every reader goes through here rather than reading `min` directly. */
export const gateMin = (g) => {
  if (!g) return null;
  if (g.min != null && g.min !== "") return Number(g.min);
  if (g.threshold != null && g.threshold !== "") return Number(g.threshold);
  return null;
};

/* An empty number field means "not set" and must survive the round trip as
   null — num() would turn it into 0, and a min of 0 is a gate that always
   passes while a target of 0 is a target nobody can miss. */
const numOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const cleanConfirmations = (c) => {
  if (!c || typeof c !== "object") return {};
  const out = {};
  Object.keys(c).slice(0, 40).forEach((k) => {
    const v = c[k];
    if (!v || typeof v !== "object") return;
    out[String(k).slice(0, 24)] = {
      at: v.at ? String(v.at) : "",
      by: v.by ? String(v.by) : "",
      byName: typeof v.byName === "string" ? v.byName.slice(0, 60) : "",
    };
  });
  return out;
};

const cleanGate = (g) => {
  const source = g && g.source === "manual" ? "manual" : "metric";
  const period = GATE_PERIODS.some((p) => p.value === (g && g.period)) ? g.period : "quarter";
  return {
    name: g && typeof g.name === "string" ? g.name.slice(0, 60) : "",
    source,
    metric: source === "manual" || !g || g.metric == null ? "" : String(g.metric),
    metricName: source === "manual" || !g || typeof g.metricName !== "string" ? "" : g.metricName,
    unit: g && g.unit === "$" ? "$" : "%",
    /* `threshold` was the old single-number shape. Read it forward so curves
       saved before gates grew a target keep gating on the same figure. */
    min: source === "manual" ? null : numOrNull(g && (g.min != null ? g.min : g.threshold)),
    target: source === "manual" ? null : numOrNull(g && g.target),
    mode: g && g.mode === "sum" ? "sum" : "avg",
    period,
    from: period === "custom" && g && g.from ? String(g.from).slice(0, 10) : "",
    to: period === "custom" && g && g.to ? String(g.to).slice(0, 10) : "",
    on: g ? g.on !== false : true,
    confirmations: source === "manual" ? cleanConfirmations(g && g.confirmations) : {},
  };
};

const cleanGates = (arr) => (Array.isArray(arr) ? arr.map(cleanGate) : []);

/** Persist shape. Drops junk the same way the layout store does. */
export const sanitizePayoutCurve = (pc) => {
  if (!pc || typeof pc !== "object") return null;
  const tiers = (Array.isArray(pc.tiers) ? pc.tiers : [])
    .map((t) => ({
      at: num(t && t.at),
      unit: t && t.unit === "$" ? "$" : "%",
      pay: num(t && t.pay),
      // A tier only carries gate config when it is switched on for that tier.
      ...(t && t.gate ? { gate: true, gates: cleanGates(t.gates) } : {}),
    }))
    .filter((t) => t.at > 0 || t.pay > 0);
  if (!tiers.length) return pc.on ? { on: true, tiers: [] } : null;
  return { on: pc.on !== false, tiers };
};

/** A blank curve, defaulted to a percent-of-target plan an admin then edits. */
export const defaultPayoutCurve = () => ({
  on: true,
  tiers: [
    { at: 10, unit: "%", pay: 4375 },
    { at: 30, unit: "%", pay: 12500 },
    { at: 50, unit: "%", pay: 15625 },
    { at: 70, unit: "%", pay: 17625 },
    { at: 90, unit: "%", pay: 19625 },
    { at: 100, unit: "%", pay: 21625 },
  ],
});

/**
 * Dollar figure shown under a tier % on the curve: share of the card's bonus
 * target (not the payout). "$" unit tiers show their raw breakpoint.
 */
export const tierThresholdDollars = (tier, target) => {
  if (!tier) return 0;
  if (tier.unit === "$") return num(tier.at);
  return (num(target) * num(tier.at)) / 100;
};

/** A tier's threshold as one absolute value, so mixed %/$ tiers share an axis. */
export const thresholdValue = (tier, target) => {
  if (!tier) return 0;
  if (tier.unit === "$") return num(tier.at);
  return (num(target) * num(tier.at)) / 100; // percent of target
};

/** Tiers sorted low→high by their real threshold, each carrying that threshold. */
export const sortedTiers = (tiers, target) =>
  (tiers || [])
    .map((t) => ({ ...t, thr: thresholdValue(t, target) }))
    .sort((a, b) => a.thr - b.thr);

/**
 * What the metric's current value pays — the highest REACHED tier only.
 * Below the lowest tier pays 0; between tiers keeps the lower tier's pay
 * (no linear blend — $116k on a 10%/30% ladder pays the 10% amount, not ~$5k).
 *
 * A tier that pays $0 is not a payout rung, it is a seeded row the admin never
 * filled in (defaultPayoutCurve lays out six). Counting one as "reached" made
 * the ladder go BACKWARDS — a team at 55% of goal dropped from the 30% tier's
 * $300 to $0 — so unfunded tiers are skipped and the last funded amount holds.
 */
const fundedTiers = (tiers, target) => sortedTiers(tiers, target).filter((t) => num(t.pay) > 0);

export const payoutForValue = (value, target, tiers) => {
  const s = fundedTiers(tiers, target);
  if (!s.length) return 0;
  const v = num(value);
  let pay = 0;
  s.forEach((t) => {
    if (v >= t.thr) pay = t.pay;
  });
  return pay;
};

/* ── gates: what they are worth right now ────────────────────────────────── */

/** The key a manual confirmation is filed under, so it expires with its period.
    Built from the gate's OWN window — never the board's date filter. */
export const gatePeriodKey = (gate, now = new Date()) => {
  const g = gate || {};
  const y = now.getFullYear();
  if (g.period === "year") return `${y}`;
  if (g.period === "month") return `${y}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  if (g.period === "custom") return `c:${g.from || ""}:${g.to || ""}`;
  return `${y}-Q${Math.floor(now.getMonth() / 3) + 1}`;
};

/** A gate's live confirmation, or null if this period has not been signed off. */
export const gateConfirmation = (gate, now = new Date()) => {
  const c = gate && gate.confirmations;
  if (!c) return null;
  return c[gatePeriodKey(gate, now)] || null;
};

/**
 * One gate's standing.
 *
 *   met      clear, this tier is not held up by it
 *   short    a metric gate reading under its minimum
 *   waiting  a manual gate nobody has confirmed for this period
 *   unbound  a metric gate with no metric or no minimum set — it cannot judge
 *            anything, so it never withholds money (an unfinished setting must
 *            not silently cost somebody their accelerator)
 *   off      switched off by the admin
 *
 * `readMetric(gate)` hands back { value, found } for the gate's metric.
 */
export const gateState = (gate, readMetric, now = new Date()) => {
  const g = gate || {};
  const base = { gate: g, name: g.name || "", min: gateMin(g), target: g.target, value: null, pct: 0 };
  if (g.on === false) return { ...base, state: "off", blocks: false };
  if (g.source === "manual") {
    const hit = gateConfirmation(g, now);
    return hit
      ? { ...base, state: "met", blocks: false, pct: 1, confirmedAt: hit.at, confirmedBy: hit.byName || hit.by }
      : { ...base, state: "waiting", blocks: true, pct: 0 };
  }
  const min = gateMin(g);
  if (!g.metric || min == null) return { ...base, state: "unbound", blocks: false };
  const read = (typeof readMetric === "function" ? readMetric(g) : null) || {};
  if (!read.found) return { ...base, state: "unbound", blocks: false };
  const value = num(read.value);
  /* How far along, against this gate's OWN minimum. 0.96 and 0.20 both fail,
     but only one of them is worth telling somebody about. */
  let pct = 0;
  if (num(min) > 0) pct = Math.max(0, Math.min(1, value / num(min)));
  else if (value > 0) pct = 1;
  return {
    ...base,
    value,
    pct,
    state: value >= num(min) ? "met" : "short",
    blocks: value < num(min),
  };
};

/** Every gate on one tier, plus whether the tier is held. */
export const tierGateStates = (tier, readMetric, now = new Date()) => {
  if (!tier || !tier.gate || !Array.isArray(tier.gates) || !tier.gates.length) {
    return { states: [], held: false };
  }
  const states = tier.gates.map((g) => gateState(g, readMetric, now));
  return { states, held: states.some((s) => s.blocks) };
};

/**
 * The index (into `sortedTiers`) of the highest FUNDED tier the value has
 * reached, so the tier the curve highlights is the one actually being paid.
 * -1 = nothing reached yet.
 */
export const reachedTierIndex = (value, target, tiers) => {
  const s = sortedTiers(tiers, target);
  const v = num(value);
  let idx = -1;
  s.forEach((t, i) => {
    if (v >= t.thr && num(t.pay) > 0) idx = i;
  });
  return idx;
};

/**
 * What the curve actually pays once gates are applied.
 *
 * Walk the reached tiers from the bottom up and stop at the first one being
 * held: the bonus falls back to the highest tier below it that is clear. Gates
 * withhold the ACCELERATOR, not the whole bonus — a team that reaches 110% on
 * revenue alone has already earned the 100% tier, and a sales-health check
 * failing above it must not claw that back.
 *
 * A blocked tier does not let the ladder skip over it either — reaching 120%
 * while 110% is held pays the 100% tier, not the 120% one.
 *
 * Only funded tiers can be the fallback: defaultPayoutCurve seeds rows an admin
 * may never fill in, and falling back onto one of those would pay $0 while a
 * real funded tier sits right below it.
 */
export const payoutWithGates = (value, target, tiers, readMetric, now = new Date()) => {
  const s = sortedTiers(tiers, target);
  const v = num(value);
  const earned = payoutForValue(v, target, tiers);
  const reached = reachedTierIndex(v, target, tiers);
  let pay = 0;
  let paidIdx = -1;
  let heldTier = null;
  let heldStates = [];
  for (let i = 0; i <= reached; i += 1) {
    const { states, held } = tierGateStates(s[i], readMetric, now);
    if (held) {
      /* Stop here and keep what the last cleared tier pays. */
      heldTier = s[i];
      heldStates = states;
      break;
    }
    if (num(s[i].pay) > 0) {
      pay = s[i].pay;
      paidIdx = i;
    }
  }
  return {
    pay: num(pay),
    earned: num(earned),
    held: num(earned) - num(pay),
    reachedIdx: reached,
    paidIdx,
    heldTier,
    heldTierIdx: heldTier ? s.indexOf(heldTier) : -1,
    gateStates: heldStates,
    isHeld: !!heldTier,
  };
};

/** A short label for a tier's threshold ("110%", "$300K"). */
export const tierLabel = (tier) => {
  if (!tier) return "";
  if (tier.unit === "$") {
    const n = num(tier.at);
    return n >= 1000 ? `$${Math.round(n / 1000)}K` : `$${Math.round(n)}`;
  }
  return `${num(tier.at)}%`;
};

/**
 * Geometry for drawing: an x in [4,96] for the current value and each tier,
 * on a domain padded a little past the lowest and highest thresholds so the end
 * markers are not flush against the edges.
 */
export const curveGeometry = (value, target, tiers) => {
  const s = sortedTiers(tiers, target);
  if (!s.length) return { xForValue: 50, ticks: [] };
  const lo = s[0].thr;
  const hi = s[s.length - 1].thr;
  const pad = Math.max((hi - lo) * 0.12, hi * 0.04, 1);
  const dLo = lo - pad;
  const dHi = hi + pad;
  const xOf = (v) => {
    const x = 4 + ((num(v) - dLo) / ((dHi - dLo) || 1)) * 92;
    return Math.max(4, Math.min(96, x));
  };
  return {
    xForValue: xOf(value),
    ticks: s.map((t, i) => ({ x: xOf(t.thr), label: tierLabel(t), pay: t.pay, thr: t.thr, i })),
  };
};
